import { Sparkles, Crown, Rocket, Zap, Star } from "lucide-react";

export type PlanKey = "trial" | "basic" | "growth" | "pro" | "ultimate";
export type BillingCycle = "monthly" | "yearly";

/**
 * واحد مصرف در ترندی اسمارت «مکالمه» است.
 * مکالمه = یک پنجرهٔ ۲۴ ساعته با یک مخاطب. از لحظه‌ای که ترندی اولین پاسخ
 * هوشمند را می‌دهد، ۲۴ ساعت باز می‌ماند و هر تعداد پیام داخل آن = ۱ مکالمه.
 */
export const CONVERSATION_RULES = [
  "هر مخاطب در هر ۲۴ ساعت = فقط ۱ مکالمه، بی‌نهایت پیام داخل آن",
  "فقط وقتی شمرده می‌شود که ترندی واقعاً پاسخ هوشمند داده باشد",
  "پاسخ‌های ثابت و کلیدواژه‌ای (بدون AI) رایگان و بی‌شمار هستند",
  "لایک، استیکر، پیام اسپم و پیام بدون پاسخ شمرده نمی‌شوند",
  "مکالمه‌ای که به اپراتور انسانی منتقل شود، به سهمیه بازگردانده می‌شود",
];

export const CONVERSATION_DEFINITION =
  "مکالمه = یک پنجرهٔ ۲۴ ساعته با یک مخاطب؛ هر تعداد پیام در این بازه فقط یک مکالمه حساب می‌شود.";

/** رفتار پیش‌فرض بعد از اتمام سهمیه: پاسخ ثابت بدون AI */
export const QUOTA_EXHAUSTED_BEHAVIOR =
  "پس از اتمام سهمیه، ترندی با پاسخ‌های ثابت و قالب‌های آماده به مخاطبان جواب می‌دهد (بدون هوش مصنوعی) تا شما بستهٔ اضافه بخرید یا دوره تمدید شود.";

export interface Plan {
  key: PlanKey;
  name: string;
  tagline: string;
  monthly: number; // toman
  yearly: number; // toman per year (already discounted)
  /** هزینهٔ هر مکالمهٔ اضافه فراتر از سهمیه (تومان). 0 یعنی غیرقابل خرید (آزمایشی). */
  perConversationPrice: number;
  features: string[];
  channelLimit: number;
  /** سهمیهٔ مکالمهٔ ماهانه. 999999 یعنی نامحدود. */
  conversationLimit: number;
  recommended?: boolean;
  trial?: boolean;
  /** @deprecated نام قدیمی conversationLimit — فقط برای سازگاری */
  tokenLimit: number;
  /** @deprecated نام قدیمی perConversationPrice — فقط برای سازگاری */
  perUserPrice: number;
  icon: React.ElementType;
  gradient: string;
}

export const UNLIMITED_CONVERSATIONS = 999999;

export function formatConversationLimit(n: number) {
  return n >= UNLIMITED_CONVERSATIONS ? "نامحدود" : n.toLocaleString("fa-IR");
}

/** بسته‌های شارژ مکالمه. قیمت بر اساس نرخ هر مکالمهٔ پلن فعال محاسبه می‌شود. */
export interface ConversationPackage {
  key: string;
  label: string;
  conversations: number;
  badge?: string;
  popular?: boolean;
  bonusPct?: number;
  gradient: string;
  desc: string;
  /** @deprecated نام قدیمی conversations — فقط برای سازگاری */
  users: number;
}

export const CONVERSATION_PACKAGES: ConversationPackage[] = [
  {
    key: "cp-100",
    label: "بستهٔ جمع‌وجور",
    conversations: 100,
    users: 100,
    gradient: "from-teal to-primary-main",
    desc: "برای روزهای پرترافیک آخر ماه",
  },
  {
    key: "cp-500",
    label: "بستهٔ استاندارد",
    conversations: 500,
    users: 500,
    popular: true,
    badge: "محبوب",
    bonusPct: 5,
    gradient: "from-primary-main to-purple",
    desc: "انتخاب بیشتر کسب‌وکارها",
  },
  {
    key: "cp-2000",
    label: "بستهٔ حرفه‌ای",
    conversations: 2000,
    users: 2000,
    badge: "۱۰٪ هدیه",
    bonusPct: 10,
    gradient: "from-purple to-secondary-main",
    desc: "مناسب کمپین‌ها و فروش فصلی",
  },
  {
    key: "cp-10000",
    label: "بستهٔ سازمانی",
    conversations: 10000,
    users: 10000,
    badge: "۱۵٪ هدیه",
    bonusPct: 15,
    gradient: "from-amber to-secondary-main",
    desc: "برای برندهای پرمخاطب",
  },
];

export function conversationPackagePrice(pkg: ConversationPackage, perConversationPrice: number) {
  return pkg.conversations * perConversationPrice;
}

export function conversationPackageBonus(pkg: ConversationPackage) {
  return pkg.bonusPct ? Math.round((pkg.conversations * pkg.bonusPct) / 100) : 0;
}

export function getConversationPackage(key: string): ConversationPackage {
  return CONVERSATION_PACKAGES.find((p) => p.key === key) ?? CONVERSATION_PACKAGES[1];
}

/** کدهای تخفیف قابل اعمال روی خرید بستهٔ مکالمه */
export type DiscountKind = "percent" | "amount" | "bonus";
export interface DiscountCode {
  code: string;
  kind: DiscountKind;
  /** درصد، مبلغ ثابت (تومان) یا تعداد مکالمهٔ هدیه */
  value: number;
  label: string;
  minAmount?: number;
  expired?: boolean;
  usedUp?: boolean;
}

export const DISCOUNT_CODES: DiscountCode[] = [
  { code: "TRENDI20", kind: "percent", value: 20, label: "۲۰٪ تخفیف روی کل خرید" },
  {
    code: "WELCOME500",
    kind: "amount",
    value: 500000,
    label: "۵۰۰٬۰۰۰ تومان تخفیف",
    minAmount: 1500000,
  },
  { code: "GIFT100", kind: "bonus", value: 100, label: "۱۰۰ مکالمهٔ هدیه" },
  { code: "NOROOZ", kind: "percent", value: 30, label: "کمپین نوروزی", expired: true },
  { code: "FRIEND50", kind: "amount", value: 50000, label: "معرفی دوستان", usedUp: true },
];

export function findDiscount(code: string) {
  return DISCOUNT_CODES.find((d) => d.code.toUpperCase() === code.trim().toUpperCase());
}

export const PLANS: Plan[] = [
  {
    key: "trial",
    name: "آزمایشی ۱۴ روزه",
    tagline: "برای آشنایی با امکانات",
    monthly: 0,
    yearly: 0,
    channelLimit: 1,
    conversationLimit: 300,
    perConversationPrice: 0,
    tokenLimit: 300,
    perUserPrice: 0,
    trial: true,
    icon: Sparkles,
    gradient: "from-grayScale-400 to-grayScale-600",
    features: [
      "اتصال ۱ کانال (اینستاگرام / تلگرام / بله / سایت)",
      "۳۰۰ مکالمهٔ هوشمند در کل دوره",
      "پاسخ خودکار پایه (تا ۵ پاسخ فعال)",
      "قالب‌های آماده پاسخ خودکار",
      "هزینهٔ پیامک: ۳۲۰ ت / پیامک",
    ],
  },
  {
    key: "basic",
    name: "پایه",
    tagline: "مناسب کسب‌وکارهای کوچک",
    monthly: 290000,
    yearly: 2784000,
    channelLimit: 2,
    conversationLimit: 1500,
    perConversationPrice: 3500,
    tokenLimit: 1500,
    perUserPrice: 3500,
    icon: Zap,
    gradient: "from-teal to-primary-main",
    features: [
      "اتصال تا ۲ کانال",
      "۱٬۵۰۰ مکالمهٔ هوشمند در ماه (پنجرهٔ ۲۴ ساعته)",
      "پاسخ ثابت و کلیدواژه‌ای نامحدود و رایگان",
      "متغیرهای داینامیک ({{name}}, {{order_id}}, ...)",
      "تأخیر بین بلوک‌ها + افکت typing",
      "زمان‌بندی فعال‌سازی + Cooldown کاربر",
      "آمار مکالمه‌ها و قیف ارسال پایه",
      "هزینهٔ پیامک: ۲۹۰ ت / پیامک (۱۰٪ ارزان‌تر)",
    ],
  },
  {
    key: "growth",
    name: "رشد",
    tagline: "محبوب‌ترین انتخاب",
    monthly: 590000,
    yearly: 5664000,
    channelLimit: 4,
    conversationLimit: 4000,
    perConversationPrice: 3000,
    tokenLimit: 4000,
    perUserPrice: 3000,
    recommended: true,
    icon: Rocket,
    gradient: "from-primary-main to-purple",
    features: [
      "اتصال تا ۴ کانال",
      "۴٬۰۰۰ مکالمهٔ هوشمند در ماه",
      "تیم تا ۳ نفر با نقش‌های مختلف",
      "Multi-step Flows + Quick Replies",
      "تشخیص زبان (FA/AR/EN) + غلط املایی",
      "AI Suggestions برای ساخت سریع بلوک‌ها",
      "Heatmap کلیدواژه‌ها (Gap Analysis)",
      "هزینهٔ پیامک: ۲۶۰ ت / پیامک (۲۰٪ ارزان‌تر)",
    ],
  },
  {
    key: "pro",
    name: "حرفه‌ای",
    tagline: "برای تیم‌های فعال",
    monthly: 1190000,
    yearly: 11424000,
    channelLimit: 8,
    conversationLimit: 12000,
    perConversationPrice: 2500,
    tokenLimit: 12000,
    perUserPrice: 2500,
    icon: Crown,
    gradient: "from-purple to-secondary-main",
    features: [
      "اتصال تا ۸ کانال",
      "۱۲٬۰۰۰ مکالمهٔ هوشمند در ماه",
      "تیم تا ۱۰ نفر + لاگ کامل فعالیت",
      "Webhook / HTTP Action و CRM Sync",
      "Sentiment Detection و انتقال خودکار به انسان",
      "A/B Testing و Conversion Funnel",
      "API اختصاصی و وبهوک سفارشی",
      "هزینهٔ پیامک: ۲۳۵ ت / پیامک (۲۷٪ ارزان‌تر)",
    ],
  },
  {
    key: "ultimate",
    name: "نامحدود",
    tagline: "برای سازمان‌ها",
    monthly: 2890000,
    yearly: 27744000,
    channelLimit: 99,
    conversationLimit: UNLIMITED_CONVERSATIONS,
    perConversationPrice: 2000,
    tokenLimit: UNLIMITED_CONVERSATIONS,
    perUserPrice: 2000,
    icon: Star,
    gradient: "from-amber to-secondary-main",
    features: [
      "کانال نامحدود + کسب‌وکار نامحدود",
      "مکالمهٔ هوشمند نامحدود (با سقف انصاف)",
      "تیم نامحدود با ساختار سازمانی",
      "همهٔ امکانات حرفه‌ای (Webhook, A/B, CRM)",
      "Multi-Persona AI و داشبورد اختصاصی",
      "SLA و مدیر حساب اختصاصی",
      "آموزش و راه‌اندازی اختصاصی",
      "هزینهٔ پیامک: ۲۱۰ ت / پیامک (کمترین قیمت — ۳۵٪ ارزان‌تر)",
    ],
  },
];

export function getPlan(key: PlanKey): Plan {
  return PLANS.find((p) => p.key === key) ?? PLANS[0];
}

/* ---- سازگاری با کدهای قدیمی (نام‌گذاری توکنی) ---- */
export type TokenPackage = ConversationPackage;
export const TOKEN_PACKAGES = CONVERSATION_PACKAGES;
export const getTokenPackage = getConversationPackage;
export function tokenPackagePrice(pkg: ConversationPackage, perUnitPrice: number) {
  return conversationPackagePrice(pkg, perUnitPrice);
}
export const tokenPackageBonus = conversationPackageBonus;
